import React, { useState, useCallback, useEffect } from "react";
import { Autocomplete, OutlinedInput } from "~/components/UI";
import { useField, FormikHandlers } from "formik";
import { useTranslation } from "react-i18next";
import api from "~/api";
import _ from "lodash";

interface FormikSelectSiteProps {
  name: string;
  defaultInputValue?: string;
  filterOptions?: (options: Api.SiteDto[], state: any) => Api.SiteDto[];
}

interface SelectSiteProps {
  name?: string;
  inputValue?: string;
  onChange?: (site?: Api.SiteDto) => void;
  onChangeValue?: (value?: string) => void;
  onBlur?: FormikHandlers["handleBlur"];
  error?: string | undefined;
  touched?: boolean;
  autoClear?: boolean;
  filterOptions?: (options: Api.PosDto[], state: any) => Api.PosDto[];
}

const requestSearchSite = _.memoize((search: string) =>
  api.sites.getAll(100, 1, { search })
);

const SelectSite = React.memo(
  ({
    onChange,
    onBlur = () => {},
    touched,
    error,
    filterOptions,
    inputValue,
  }: SelectSiteProps) => {
    const [value, setValue] = useState<string | undefined>(inputValue || "");
    const [loading, setLoading] = useState(false);
    const [searchResults, setSearchResults] = useState<Api.SiteDto[]>([]);
    const [site, setSite] = useState<Partial<Api.SiteDto> | null>(null);
    const { t } = useTranslation();

    const searchSite = useCallback(
      _.debounce((name: string) => {
        setLoading(true);
        requestSearchSite(name)
          .then(({ data }) => {
            setSearchResults(data.data);
          })
          .finally(() => {
            setLoading(false);
          });
      }, 500),
      []
    );

    const onChangeInputValue = useCallback(
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setValue(e.target.value);
        searchSite(e.target.value || "");
      },
      [setValue, searchSite]
    );

    const onChangeAutocomplete = useCallback(
      (_e: React.ChangeEvent<{}>, newValue: any) => {
        if (newValue) {
          setValue(`${newValue.name} (ID:${newValue.siteId})`);
          setSite(newValue);
        } else {
          setValue("");
          setSite(null);
        }
        onChange && onChange(newValue);
      },
      [onChange, setValue]
    );

    const getOptionSelected = useCallback(
      (o: Api.SiteDto, value: Api.SiteDto) => o.siteId === value.siteId,
      []
    );

    const getOptionLabel = useCallback(
      (o: Api.SiteDto) => `${o.name} (ID:${o.siteId})`,
      []
    );

    useEffect(() => {
      searchSite("");
    }, [searchSite]);

    useEffect(() => {
      if (!inputValue && value) {
        setValue("");
        setSite(null);
      }
    }, [inputValue]);

    return (
      <Autocomplete
        value={site}
        onChange={onChangeAutocomplete}
        getOptionSelected={getOptionSelected}
        onBlur={onBlur}
        noOptionsText={t("text.noResultsFound")}
        options={searchResults}
        filterOptions={filterOptions}
        getOptionLabel={getOptionLabel}
        renderInput={(params: any) => (
          <OutlinedInput
            loading={loading}
            className={params.InputProps.className}
            endAdornment={params.InputProps.endAdornment}
            inputProps={_.omit(params.inputProps, "value")}
            value={value}
            touched={touched}
            error={error}
            onChange={onChangeInputValue}
            fullWidth
          />
        )}
      />
    );
  }
);

const FormikContainer = ({
  name,
  defaultInputValue,
}: FormikSelectSiteProps & SelectSiteProps) => {
  const [field, { error, touched }] = useField(name);
  const onChange = useCallback(
    (site?: Api.SiteDto) => {
      field.onChange({
        target: {
          name,
          value: site ? site.siteId : "",
        },
      });
    },
    [field]
  );

  return (
    <SelectSite
      onChange={onChange}
      touched={touched}
      error={error}
      inputValue={defaultInputValue || field.value}
    />
  );
};

export default React.memo(FormikContainer);
